import { useEffect, useState } from 'react';
import { Outlet, useNavigate, useParams } from 'react-router-dom';
import { faXmark } from '@fortawesome/pro-regular-svg-icons';

import { IS_STAGING, PATH } from '@constants';
import { getPlatformAppsTemplatesTags } from '@thunks/platformApps.thunks';
import {
  platformAppsLoadersSelector,
  platformAppsTemplateSelector,
} from '@selectors';
import { useDispatch, useSelector } from '@hooks';

import { Helmet, Icon, Render } from '@components';
import { Layout } from '@components/Layouts';
import { AppsTabs } from '@components/Ui';
import { PlatformAppsHeaderCenter } from '@components/Ui/PlatformApps';

const TABS = [
  {
    title: 'All Apps',
    path: PATH.APPS,
  },
  {
    title: 'Installed Apps',
    path: PATH.APPS_INSTALLED,
  },
  {
    title: 'Legacy Apps',
    path: PATH.APPS_LEGACY,
  },
];

if (!IS_STAGING) {
  TABS.push({ title: 'Dev Schema', path: PATH.APPS_DEV_SCHEMA });
}

export const PlatformAppsLayout = () => {
  const { appName } = useParams();
  const navigate = useNavigate();

  const dispatch = useDispatch();
  const template = useSelector(platformAppsTemplateSelector(appName!));
  const { templatesTagsFetched, templatesTagsLoading } = useSelector(
    platformAppsLoadersSelector,
  );

  const [title, setTitle] = useState('');

  useEffect(() => {
    // https://linear.app/apolo/issue/ENG-751/add-apps-page-filtered-by-template-name
    if (appName) {
      setTitle('Installed Apps');
    }
  }, [appName]);

  useEffect(() => {
    const run = async () => {
      try {
        dispatch(getPlatformAppsTemplatesTags());
      } catch (error) {
        /**
         * Continue regardless of the error
         */
      }
    };

    if (!templatesTagsFetched && !templatesTagsLoading) {
      run();
    }
  }, [templatesTagsFetched, templatesTagsLoading, dispatch]);

  return (
    <Layout title={title} backNavigation={Boolean(appName)}>
      <Helmet title={title} />
      <Layout.Content>
        <div className="mb-14 flex items-center justify-between">
          <Render if={appName}>
            <div className="flex items-center justify-center gap-1.5 rounded-full bg-rebecca px-6 py-2 capitalize text-white">
              {template?.title || appName}

              <Icon
                icon={faXmark}
                className="cursor-pointer text-[20px]"
                onClick={() =>
                  // history.length > 2 to ensure there is a meaningful navigation history.
                  window.history.length > 2 ? navigate(-1) : navigate(PATH.APPS)
                }
              />
            </div>
          </Render>

          <Render if={!appName}>
            <AppsTabs tabs={TABS} setTitle={setTitle} />
          </Render>

          {/* Renders apps page header partials like search field, view type selector */}
          <PlatformAppsHeaderCenter />
        </div>

        <Outlet />
      </Layout.Content>
    </Layout>
  );
};
